import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Map, Upload } from 'lucide-react';
import api from '../../lib/api';
import Button from '../Button';
import toast from 'react-hot-toast';

interface JurisdictionRow {
  state_code: string;
  active_map_cycle: string;
  updated_at: string;
}

interface ImportResultRow {
  ok: boolean;
  file: string;
  key: string;
  chamber: string;
  district: string;
}

interface ImportErrorRow {
  file: string;
  error: string;
}

/**
 * District map PDF bulk upload (ZIP or multi-PDF) + jurisdiction active cycle.
 * Used from Data Import (tab) and optionally from a standalone admin route.
 */
const DistrictMapsImportTab: React.FC = () => {
  const [settings, setSettings] = useState<JurisdictionRow[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const [stateCode, setStateCode] = useState('CO');
  const [mapCycle, setMapCycle] = useState('2022');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [promoteMapCycleToActive, setPromoteMapCycleToActive] = useState(true);

  const [zipFile, setZipFile] = useState<File | null>(null);
  const [pdfFiles, setPdfFiles] = useState<FileList | null>(null);
  const [zipSubmitting, setZipSubmitting] = useState(false);
  const [pdfSubmitting, setPdfSubmitting] = useState(false);
  const [lastZipResult, setLastZipResult] = useState<{
    imported: number;
    results: ImportResultRow[];
    errors: ImportErrorRow[];
  } | null>(null);
  const [lastPdfResult, setLastPdfResult] = useState<{
    imported: number;
    results: ImportResultRow[];
    errors: ImportErrorRow[];
  } | null>(null);

  const [activeCycleEdit, setActiveCycleEdit] = useState('');
  const [patchingCycle, setPatchingCycle] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const res = await api.get('/district-maps/jurisdiction-settings');
      const rows: JurisdictionRow[] = res.data?.data?.settings || [];
      setSettings(rows);
      const co = rows.find((r) => r.state_code === 'CO');
      if (co) setActiveCycleEdit(co.active_map_cycle);
    } catch {
      toast.error('Could not load jurisdiction settings (system admin required)');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const submitZip = async () => {
    if (!zipFile) {
      toast.error('Choose a ZIP file (PDFs inside must be named like HD01.pdf, HD-7.pdf, or SD12.pdf).');
      return;
    }
    if (!mapCycle.trim()) {
      toast.error('Map cycle is required (e.g. 2022).');
      return;
    }
    const fd = new FormData();
    fd.append('archive', zipFile);
    fd.append('state_code', stateCode.trim().toUpperCase());
    fd.append('map_cycle', mapCycle.trim());
    fd.append('replace_existing', replaceExisting ? 'true' : 'false');
    fd.append('set_active_cycle', promoteMapCycleToActive ? 'true' : 'false');
    setZipSubmitting(true);
    setLastZipResult(null);
    try {
      const res = await api.post('/district-maps/bulk-zip', fd, {
        timeout: 600000,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data?.data;
      setLastZipResult({
        imported: d?.imported ?? 0,
        results: d?.results ?? [],
        errors: d?.errors ?? []
      });
      toast.success(`Imported ${d?.imported ?? 0} district map file(s)`);
      await loadSettings();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'ZIP upload failed';
      toast.error(msg);
    } finally {
      setZipSubmitting(false);
    }
  };

  const submitPdfs = async () => {
    if (!pdfFiles || pdfFiles.length === 0) {
      toast.error('Choose one or more PDFs named HD{n}.pdf or SD{n}.pdf (hyphen optional).');
      return;
    }
    if (!mapCycle.trim()) {
      toast.error('Map cycle is required.');
      return;
    }
    const fd = new FormData();
    for (let i = 0; i < pdfFiles.length; i += 1) {
      fd.append('files', pdfFiles[i]);
    }
    fd.append('state_code', stateCode.trim().toUpperCase());
    fd.append('map_cycle', mapCycle.trim());
    fd.append('replace_existing', replaceExisting ? 'true' : 'false');
    fd.append('set_active_cycle', promoteMapCycleToActive ? 'true' : 'false');
    setPdfSubmitting(true);
    setLastPdfResult(null);
    try {
      const res = await api.post('/district-maps/bulk-pdf', fd, {
        timeout: 600000,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data?.data;
      setLastPdfResult({
        imported: d?.imported ?? 0,
        results: d?.results ?? [],
        errors: d?.errors ?? []
      });
      toast.success(`Imported ${d?.imported ?? 0} district map file(s)`);
      await loadSettings();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'PDF upload failed';
      toast.error(msg);
    } finally {
      setPdfSubmitting(false);
    }
  };

  const saveActiveCycleOnly = async () => {
    const st = stateCode.trim().toUpperCase();
    if (st.length !== 2) {
      toast.error('State code must be two letters.');
      return;
    }
    if (!activeCycleEdit.trim()) {
      toast.error('Active map cycle is required.');
      return;
    }
    setPatchingCycle(true);
    try {
      await api.patch(`/district-maps/jurisdiction-settings/${st}`, {
        active_map_cycle: activeCycleEdit.trim()
      });
      toast.success('Active map cycle updated');
      await loadSettings();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update active cycle';
      toast.error(msg);
    } finally {
      setPatchingCycle(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <Map className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          District maps
        </h2>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
          Upload Colorado (or other state) House and Senate district PDFs. Filenames must start with{' '}
          <span className="font-mono text-text-primary dark:text-text-dark-primary">HD</span> or{' '}
          <span className="font-mono text-text-primary dark:text-text-dark-primary">SD</span>, then the district
          number, then <span className="font-mono">.pdf</span>. A hyphen is optional, for example{' '}
          <span className="font-mono text-text-primary dark:text-text-dark-primary">HD01.pdf</span>,{' '}
          <span className="font-mono text-text-primary dark:text-text-dark-primary">HD-01.pdf</span>,{' '}
          <span className="font-mono text-text-primary dark:text-text-dark-primary">SD35.pdf</span>. Any folder layout
          inside the ZIP is fine.
        </p>
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2 max-w-3xl">
          This upload runs immediately and does not enqueue a Data Import job. Per-file results and errors appear on
          this tab after each upload; they are not listed under Job History.
        </p>
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Jurisdiction settings</h3>
        {settingsLoading ? (
          <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading…
          </div>
        ) : (
          <ul className="text-sm text-text-secondary dark:text-text-dark-secondary space-y-1">
            {settings.map((s) => (
              <li key={s.state_code}>
                <span className="font-medium text-text-primary dark:text-text-dark-primary">{s.state_code}</span>
                {' — active map cycle: '}
                <span className="font-mono">{s.active_map_cycle}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-border-light dark:border-border-dark">
          <div>
            <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              State
            </label>
            <input
              className="input w-24 uppercase"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              maxLength={2}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Active map cycle
            </label>
            <input
              className="input w-40"
              value={activeCycleEdit}
              onChange={(e) => setActiveCycleEdit(e.target.value)}
              placeholder="2022"
            />
          </div>
          <Button variant="secondary" size="md" onClick={saveActiveCycleOnly} disabled={patchingCycle}>
            {patchingCycle ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save active cycle'}
          </Button>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Upload options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              State code
            </label>
            <input
              className="input w-full max-w-xs uppercase"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              maxLength={2}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Map cycle for this upload
            </label>
            <input
              className="input w-full max-w-xs"
              value={mapCycle}
              onChange={(e) => setMapCycle(e.target.value)}
              placeholder="2022"
            />
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 max-w-lg">
              Map cycle labels which redistricting plan this upload belongs to (e.g.{' '}
              <span className="font-mono">2022</span> for Colorado’s current post-2020 maps). It is stored on each row
              and in storage paths. The active map cycle for a state (see jurisdiction settings above) decides which
              PDFs are linked from legislator pages.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-dark-primary">
          <input
            type="checkbox"
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)}
            className="rounded border-border-light dark:border-border-dark"
          />
          Replace existing rows for this state and map cycle before importing (deletes prior DB rows and storage
          objects for that cycle)
        </label>
        <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-dark-primary">
          <input
            type="checkbox"
            checked={promoteMapCycleToActive}
            onChange={(e) => setPromoteMapCycleToActive(e.target.checked)}
            className="rounded border-border-light dark:border-border-dark"
          />
          Set this map cycle as the active cycle for the state after a successful import
        </label>
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <Upload className="w-5 h-5" />
          ZIP upload (up to 100 MB)
        </h3>
        <input
          type="file"
          accept=".zip,application/zip"
          className="block text-sm text-text-secondary dark:text-text-dark-secondary"
          onChange={(e) => setZipFile(e.target.files?.[0] || null)}
        />
        <Button variant="primary" size="md" onClick={submitZip} disabled={zipSubmitting}>
          {zipSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Uploading…
            </>
          ) : (
            'Upload ZIP'
          )}
        </Button>
        {lastZipResult && (
          <div className="text-sm text-text-secondary dark:text-text-dark-secondary space-y-1">
            <p>
              Imported:{' '}
              <span className="font-medium text-text-primary dark:text-text-dark-primary">{lastZipResult.imported}</span>
            </p>
            {lastZipResult.errors.length > 0 && (
              <div>
                <p className="font-medium text-text-primary dark:text-text-dark-primary">Errors</p>
                <ul className="list-disc pl-5 font-mono text-xs">
                  {lastZipResult.errors.map((er) => (
                    <li key={er.file}>
                      {er.file}: {er.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Multi-file PDF upload</h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          Same naming rules as the ZIP path; useful for small batches (max 120 files per request, 45 MB each).
        </p>
        <input
          type="file"
          accept=".pdf,application/pdf"
          multiple
          className="block text-sm text-text-secondary dark:text-text-dark-secondary"
          onChange={(e) => setPdfFiles(e.target.files)}
        />
        <Button variant="secondary" size="md" onClick={submitPdfs} disabled={pdfSubmitting}>
          {pdfSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Uploading…
            </>
          ) : (
            'Upload PDFs'
          )}
        </Button>
        {lastPdfResult && (
          <div className="text-sm text-text-secondary dark:text-text-dark-secondary space-y-1">
            <p>
              Imported:{' '}
              <span className="font-medium text-text-primary dark:text-text-dark-primary">{lastPdfResult.imported}</span>
            </p>
            {lastPdfResult.errors.length > 0 && (
              <div>
                <p className="font-medium text-text-primary dark:text-text-dark-primary">Errors</p>
                <ul className="list-disc pl-5 font-mono text-xs">
                  {lastPdfResult.errors.map((er) => (
                    <li key={er.file}>
                      {er.file}: {er.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DistrictMapsImportTab;
